import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { PRODUCT_RESOLVED_THEMES } from "./theme-contract";
import { getCurrentTheme, onThemeChanged } from "./theme";

export function createMUDSFluentTheme(resolvedTheme) {
  const color = resolvedTheme?.color || PRODUCT_RESOLVED_THEMES.dark.color;
  const base = resolvedTheme?.appearance === "light" ? webLightTheme : webDarkTheme;
  return {
    ...base,
    colorBrandBackground: color.brandPrimary,
    colorBrandBackgroundHover: color.brandHover,
    colorBrandBackgroundPressed: color.brandPressed,
    colorBrandBackgroundSelected: color.brandPrimary,
    colorBrandBackgroundStatic: color.brandPrimary,
    colorBrandBackground2: color.brandPrimary,
    colorBrandBackground2Hover: color.brandHover,
    colorBrandBackground2Pressed: color.brandPressed,
    colorBrandForeground1: color.brandPrimary,
    colorBrandForeground2: color.brandPrimary,
    colorBrandForeground1Hover: color.brandHover,
    colorBrandForeground1Pressed: color.brandPressed,
    colorBrandForegroundLink: color.brandPrimary,
    colorBrandForegroundLinkHover: color.brandHover,
    colorBrandForegroundLinkPressed: color.brandPressed,
    colorNeutralBackground1: color.backgroundSurface1,
    colorBrandStroke1: color.brandPrimary,
    colorBrandStroke2Contrast: base.colorNeutralStroke2,
    colorCompoundBrandBackground: color.brandPrimary,
    colorCompoundBrandBackgroundHover: color.brandHover,
    colorCompoundBrandBackgroundPressed: color.brandPressed,
    colorCompoundBrandForeground1: color.brandPrimary,
    colorCompoundBrandForeground1Hover: color.brandHover,
    colorCompoundBrandForeground1Pressed: color.brandPressed,
    colorCompoundBrandStroke: color.brandPrimary,
    colorCompoundBrandStrokeHover: color.brandHover,
    colorCompoundBrandStrokePressed: color.brandPressed,
    colorNeutralBackground2: resolvedTheme?.appearance === "light" ? "#FFFFFF" : color.backgroundSurface2,
    colorNeutralBackground3: color.backgroundSurface3,
    colorNeutralBackground4: color.backgroundCanvas,
    colorNeutralBackgroundDisabled: "#F0F0F0",
    colorNeutralBackground8: color.backgroundInput,
    colorNeutralForeground1: color.textPrimary,
    colorNeutralForeground2: color.textSecondary,
    colorNeutralForeground3: color.textMuted,
    colorNeutralForegroundDisabled: color.textDisabled,
    colorNeutralForegroundInverted: color.textInverse,
    colorNeutralForegroundOnBrand: color.textOnBrand,
    colorNeutralForeground2BrandHover: color.brandHover,
    colorNeutralForeground2BrandPressed: color.brandPressed,
    colorNeutralForeground2BrandSelected: color.brandPrimary,
    colorNeutralForeground3BrandHover: color.brandHover,
    colorNeutralForeground3BrandPressed: color.brandPressed,
    colorNeutralForeground3BrandSelected: color.brandPrimary,
    colorNeutralStroke1: color.borderDefault,
    colorNeutralStrokeAccessible: color.borderStrong,
    colorStrokeFocus1: color.borderFocus,
    colorStrokeFocus2: color.borderFocus,
    colorPaletteGreenForeground1: color.success,
    colorPaletteYellowForeground1: color.warning,
    colorPaletteRedForeground1: color.danger,
    colorPaletteBlueForeground2: color.information,
    colorStatusSuccessForeground1: color.success,
    colorStatusWarningForeground1: color.warning,
    colorStatusDangerForeground1: color.danger,
    colorStatusDangerBackground1: color.danger,
    primaryColorBackground: color.backgroundCanvas,
    secondaryColorBackground: color.backgroundSurface2,
    primary: color.brandPrimary,
    primaryHover: color.brandHover,
    primaryPressed: color.brandPressed,
    gray3: color.textMuted,
    "Gray-3": color.textMuted
  };
}

export const MUDSProductDarkTheme = createMUDSFluentTheme(PRODUCT_RESOLVED_THEMES.dark);
// Compatibility for isolated stories; application roots use the factory above.
export const MUDSDarkTheme = MUDSProductDarkTheme;

export function RuntimeFluentProvider({ children }) {
  const [resolvedTheme, setResolvedTheme] = useState(getCurrentTheme);

  useEffect(() => onThemeChanged(() => setResolvedTheme(getCurrentTheme())), []);

  const fluentTheme = useMemo(() => createMUDSFluentTheme(resolvedTheme), [resolvedTheme]);
  return (
    <FluentProvider style={{ backgroundColor: resolvedTheme.color.backgroundCanvas }} theme={fluentTheme}>
      {children}
    </FluentProvider>
  );
}

RuntimeFluentProvider.propTypes = { children: PropTypes.node };
